import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, LogOut, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIMIT = 15;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
};

const inputCls =
  "w-full rounded-full border border-[rgba(26,24,23,0.14)] bg-white px-6 py-4 text-sm text-ink placeholder:text-[#8A8079] outline-none transition-colors duration-300 focus:border-gold";

export default function AdminPage() {
  const [auth, setAuth] = useState(null); // null = checking, false = signed out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState({ total: 0, items: [] });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/me`, { withCredentials: true })
      .then((res) => setAuth(res.data))
      .catch(() => setAuth(false));
  }, []);

  const load = useCallback(async (s, p) => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/waitlist/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/waitlist`, {
          params: { search: s || undefined, skip: p * LIMIT, limit: LIMIT },
          withCredentials: true,
        }),
      ]);
      setStats(statsRes.data);
      setRows(listRes.data);
    } catch (e) {
      if (e.response?.status === 401) setAuth(false);
      else toast.error("Couldn't load the waitlist.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth) load(search, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, page]);

  useEffect(() => {
    if (!auth) return;
    const t = setTimeout(() => {
      setPage(0);
      load(search, 0);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const login = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/login`,
        { email, password },
        { withCredentials: true }
      );
      setAuth(data);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch {}
    setAuth(false);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/waitlist/export`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "841-waitlist.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand">
        <p className="font-serif text-xl italic text-[#8A8079]">One moment…</p>
      </div>
    );
  }

  if (!auth) {
    return (
      <div
        data-testid="admin-login"
        className="relative flex min-h-screen items-center justify-center overflow-hidden bg-sand px-4"
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 20%, rgba(245, 230, 227, 0.7) 0%, rgba(250, 246, 240, 0) 65%)",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-md rounded-[2rem] border border-white/70 bg-white/85 p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.05)] backdrop-blur-xl"
        >
          <div className="flex items-baseline justify-center gap-2">
            <span className="font-serif text-3xl tracking-tight text-ink">841</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold-dark">wellness</span>
          </div>
          <h1 className="mt-6 font-serif text-2xl tracking-tight text-ink">Admin sign in</h1>
          <p className="mt-2 text-sm text-[#8A8079]">This area is for the 841 team only.</p>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-8 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              className={inputCls}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={inputCls}
            />
            {loginError && (
              <p
                data-testid="admin-login-error"
                className="rounded-2xl bg-red-50 px-4 py-3 text-left text-sm text-red-800"
              >
                {loginError}
              </p>
            )}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loggingIn}
              className="w-full rounded-full bg-ink px-8 py-4 text-sm tracking-wide text-sand transition-colors duration-300 hover:bg-gold-dark disabled:opacity-60"
            >
              {loggingIn ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <Link
            to="/"
            data-testid="admin-login-back"
            className="mt-6 inline-block text-xs text-[#8A8079] transition-colors hover:text-ink"
          >
            ← Back to 841 wellness
          </Link>
        </motion.div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(rows.total / LIMIT));

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sand">
      <header className="sticky top-0 z-40 border-b border-[rgba(26,24,23,0.08)] bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-4 sm:px-8">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-2xl tracking-tight text-ink">841</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold-dark">
              wellness · admin
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="text-sm text-[#5C5450] transition-colors hover:text-ink"
            >
              View site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-sm text-[#5C5450] transition-colors hover:text-ink"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">Waitlist</h1>
          <p className="mt-2 text-sm text-[#8A8079]">
            Every woman who has raised her hand for 841.
          </p>
        </motion.div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { label: "Total sign-ups", value: stats?.total, testId: "stat-total" },
            { label: "Last 7 days", value: stats?.last_7_days, testId: "stat-week" },
            { label: "Today", value: stats?.today, testId: "stat-today" },
          ].map((s) => (
            <div
              key={s.testId}
              data-testid={s.testId}
              className="rounded-3xl border border-white/70 bg-white/85 px-7 py-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)] backdrop-blur-xl"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-dark">
                {s.label}
              </p>
              <p className="mt-2 font-serif text-4xl text-ink">{s.value ?? "—"}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-sm flex-1">
            <Search
              size={16}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-[#8A8079]"
            />
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className={`${inputCls} py-3 pl-11 pr-5`}
            />
          </div>
          <button
            data-testid="export-csv-button"
            onClick={exportCsv}
            disabled={exporting}
            className="flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 text-sm text-sand transition-colors duration-300 hover:bg-gold-dark disabled:opacity-60"
          >
            <Download size={15} /> {exporting ? "Preparing…" : "Export CSV"}
          </button>
        </div>

        <div className="mt-6 overflow-hidden rounded-3xl border border-[rgba(26,24,23,0.08)] bg-white">
          <div className="overflow-x-auto">
            <table data-testid="admin-table" className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[rgba(26,24,23,0.08)] text-[11px] uppercase tracking-[0.18em] text-[#8A8079]">
                  <th className="px-6 py-4 font-semibold">Name</th>
                  <th className="px-6 py-4 font-semibold">Email</th>
                  <th className="px-6 py-4 font-semibold">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {rows.items.map((r) => (
                  <tr
                    key={r.id || r.email}
                    data-testid={`admin-row-${r.email}`}
                    className="border-b border-[rgba(26,24,23,0.05)] transition-colors last:border-0 hover:bg-blush-light/40"
                  >
                    <td className="px-6 py-4 text-ink">{r.name || "—"}</td>
                    <td className="px-6 py-4 text-[#4A433F]">{r.email}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-[#8A8079]">
                      {formatDate(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.items.length === 0 && !loading && (
            <p
              data-testid="admin-empty-state"
              className="px-6 py-12 text-center font-serif text-lg italic text-[#8A8079]"
            >
              No sign-ups found.
            </p>
          )}
          {loading && (
            <p className="px-6 py-12 text-center text-sm text-[#8A8079]">Loading…</p>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between text-sm text-[#5C5450]">
          <p data-testid="admin-pagination-info">
            Page {page + 1} of {totalPages} · {rows.total} sign-ups
          </p>
          <div className="flex gap-2">
            <button
              data-testid="admin-prev-page"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-full border border-[rgba(26,24,23,0.14)] px-5 py-2 transition-colors hover:border-gold disabled:opacity-40"
            >
              Previous
            </button>
            <button
              data-testid="admin-next-page"
              disabled={(page + 1) * LIMIT >= rows.total}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-full border border-[rgba(26,24,23,0.14)] px-5 py-2 transition-colors hover:border-gold disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
